import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Play } from 'lucide-react';
import { TRADING_PAIRS, STRATEGIES, EXPIRY_TIMES } from '../mock/mock';
import { api } from '../lib/api';

// Reusable signal terminal used both on landing (mini) and dashboard (full).
export default function SignalTerminal({
  fixedPair = null,
  interactive = true,
  autoRotate = false,
  defaultExpiry = 'M5',
}) {
  const [pair, setPair] = useState(fixedPair || 'EUR/USD OTC');
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | scanning | ready
  const [signal, setSignal] = useState(null);
  const [utc, setUtc] = useState('');
  const [strategyIdx, setStrategyIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      const d = new Date();
      setUtc(d.toISOString().substring(11, 19));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-run on mount for landing hero
  useEffect(() => {
    if (autoRotate) startScan();
  }, []);

  const startScan = () => {
    if (phase === 'scanning') return;
    setPhase('scanning');
    setProgress(0);
    setSignal(null);
    setStrategyIdx(0);

    let p = 0;
    let apiResult = null;
    // Fire API call in parallel
    api.generateSignal(pair, expiry).then((r) => { if (r.ok) apiResult = r; });

    const scanInterval = setInterval(() => {
      p += Math.max(2, Math.floor(Math.random() * 8));
      if (p >= 100) {
        p = 100;
        clearInterval(scanInterval);
        // Fallback: if API didn't respond in time, synthesize
        const s = apiResult || {
          pair, expiry,
          direction: Math.random() > 0.5 ? 'CALL' : 'PUT',
          confidence: 88 + Math.floor(Math.random() * 12),
          entryTime: new Date(Date.now() + 60000).toISOString().substring(11, 19),
        };
        setSignal(s);
        setPhase('ready');
      }
      setProgress(p);
      setStrategyIdx((i) => (i + 1) % STRATEGIES.length);
    }, 220);
  };

  const dirBadge = useMemo(() => {
    if (!signal) return { label: `△ CALL · ${pair}`, cls: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10' };
    return signal.direction === 'CALL'
      ? { label: `△ CALL · ${signal.pair}`, cls: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10' }
      : { label: `▽ PUT · ${signal.pair}`, cls: 'text-rose-300 border-rose-400/40 bg-rose-500/10' };
  }, [signal, pair]);

  return (
    <div className="glass-strong rounded-2xl p-5 sm:p-6 terminal-scan relative fade-up">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="live-dot" />
          <span className="text-[11px] font-mono tracking-[0.25em] text-rose-300">LIVE</span>
        </div>
        <div className="font-mono text-sm text-slate-300">
          <span className="text-white">{utc}</span> <span className="text-slate-500">UTC</span>
        </div>
      </div>

      {/* Direction badge (floating) */}
      <div className={`absolute -top-3 left-6 rounded-full px-3 py-1 border ${dirBadge.cls} text-xs font-semibold tracking-wider`}>
        {dirBadge.label}
      </div>

      <div className="mt-4 text-center">
        <div className="text-[11px] tracking-[0.3em] text-cyan-300/70">AI MARKET SCANNER</div>
        <div className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-white">
          {phase === 'scanning' ? 'Synchronizing Signal' : phase === 'ready' ? 'Signal Ready' : 'Idle · Awaiting Scan'}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-5">
        <div className="flex justify-between text-[10px] tracking-[0.3em] text-slate-400 mb-1">
          <span>ANALYZING</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 w-full bg-slate-800/70 rounded-full overflow-hidden">
          <div className="h-full progress-cyan transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Metrics grid */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="ENTRY" value={signal ? signal.entryTime : '—'} />
        <Metric label="EXPIRY" value={expiry} />
        <Metric label="CONFIDENCE" value={signal ? `${signal.confidence}%` : '—'} accent={signal && signal.confidence >= 92} />
        <Metric label="STATUS" value={phase === 'scanning' ? 'Scanning' : phase === 'ready' ? 'Confirmed' : 'Idle'} />
      </div>

      {/* Strategy log */}
      <div className="mt-5 rounded-lg border border-cyan-400/10 bg-black/40 p-3 font-mono text-[11px] leading-6 text-cyan-200/80 min-h-[92px]">
        <div>$ Checking {STRATEGIES[strategyIdx]}...</div>
        <div>$ Scanning Liquidity Zones...</div>
        <div>$ Applying Final AI Validation...</div>
      </div>

      {/* Interactive controls */}
      {interactive && (
        <div className="mt-4 space-y-3">
          <label className="block text-xs tracking-[0.25em] text-cyan-300/80">
            PAIR
            <select
              value={pair}
              onChange={(e) => setPair(e.target.value)}
              disabled={!!fixedPair}
              className="field-dark mt-1 w-full rounded-md px-3 py-2 text-sm"
            >
              {TRADING_PAIRS.map((p) => <option key={p} value={p} className="bg-slate-900">{p}</option>)}
            </select>
          </label>
          <div>
            <div className="text-xs tracking-[0.25em] text-cyan-300/80 mb-2">EXPIRATION TIME</div>
            <div className="grid grid-cols-3 gap-2">
              {EXPIRY_TIMES.map((p) => {
                const active = expiry === p;
                return (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setExpiry(p)}
                    className={`rounded-md px-3 py-2 text-sm font-mono tracking-wider transition-all border ${
                      active
                        ? 'border-cyan-400/70 bg-cyan-500/15 text-cyan-200 shadow-[0_0_0_2px_rgba(34,211,238,0.25)_inset]'
                        : 'border-cyan-400/15 bg-black/40 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <button onClick={startScan} className="mt-5 w-full btn-cyan rounded-lg px-4 py-3 text-sm tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2">
        {phase === 'scanning' ? (
          <>
            <span className="live-dot" style={{ background: '#0369a1', boxShadow: '0 0 10px #0284c7' }} />
            Scanning...
          </>
        ) : (
          <>
            <Play className="h-4 w-4" /> Generate New Signal
          </>
        )}
      </button>

      {/* Engine label */}
      <div className="absolute right-4 -bottom-3 rounded-full px-3 py-1 text-[10px] tracking-[0.3em] border border-cyan-400/30 bg-black/70 text-cyan-200">
        Engine • 33X
      </div>

      {/* Big direction reveal (dashboard use) */}
      {phase === 'ready' && signal && (
        <div className="mt-5 rounded-xl border border-cyan-400/20 bg-black/50 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {signal.direction === 'CALL' ? (
              <TrendingUp className="h-6 w-6 text-emerald-300" />
            ) : (
              <TrendingDown className="h-6 w-6 text-rose-300" />
            )}
            <div>
              <div className="text-[10px] tracking-[0.3em] text-slate-400">DIRECTION</div>
              <div className={`font-display text-2xl font-bold ${signal.direction === 'CALL' ? 'text-emerald-300' : 'text-rose-300'}`}>{signal.direction}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] text-slate-400">PAIR</div>
            <div className="font-mono text-white text-lg">{signal.pair}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-cyan-400/10 bg-black/40 px-3 py-2">
      <div className="text-[10px] tracking-[0.25em] text-slate-400">{label}</div>
      <div className={`font-mono text-sm ${accent ? 'text-emerald-300' : 'text-white'}`}>{value}</div>
    </div>
  );
}
